import { useState } from "react";
import { ExternalLink, FileText } from "lucide-react";
import { driveFileId, fetchDriveFile, useDriveStatus } from "@/api/drive";
import type { Expense } from "@/api/finance";
import { DocumentViewer } from "@/components/ui/document-viewer";

type Doc = { noun: "bill" | "proof"; title: string; url: string };

const LINK = "inline-flex items-center gap-1 text-primary hover:underline";

/**
 * An expense's bill and payment proof. A file the app can read back from the company Drive
 * opens in the viewer over the page; any other link (a pasted one while Drive is not
 * connected, a voided expense's, a page outside Drive) opens in a new tab.
 */
export function ExpenseDocs({ expense }: { expense: Expense }) {
  const drive = useDriveStatus();
  const [open, setOpen] = useState<Doc | null>(null);
  // While Drive "needs reconnecting" the viewer still opens, to say so.
  const connected = drive.data !== undefined && drive.data.status !== "not_connected";
  const viewer = connected && expense.voided_at === null;
  const docs = (
    [
      { noun: "bill", title: `Bill: ${expense.description}`, url: expense.bill_url },
      { noun: "proof", title: `Payment proof: ${expense.description}`, url: expense.proof_url },
    ] satisfies Doc[]
  ).filter((doc) => doc.url !== "");
  if (docs.length === 0) return null;

  return (
    <>
      <span className="flex flex-wrap gap-3 text-xs">
        {docs.map((doc) =>
          viewer && driveFileId(doc.url) ? (
            <button key={doc.noun} type="button" className={LINK} onClick={() => setOpen(doc)}>
              <FileText className="h-3 w-3" aria-hidden />
              View {doc.noun}
            </button>
          ) : (
            <a key={doc.noun} href={doc.url} target="_blank" rel="noreferrer" className={LINK}>
              <ExternalLink className="h-3 w-3" aria-hidden />
              View {doc.noun}
            </a>
          ),
        )}
      </span>
      {open && (
        <DocumentViewer
          title={open.title}
          load={() => fetchDriveFile(driveFileId(open.url) ?? "")}
          onClose={() => setOpen(null)}
          externalUrl={open.url}
        />
      )}
    </>
  );
}
